import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { api, imgUrl, errMsg } from "@/lib/api";
import { OPTION_KEYS, useOptionDefs } from "@/lib/options";
import { BRANDS } from "@/lib/i18n";

const input = "w-full h-10 px-3 bg-[#1F2230] border border-[#3B4157] text-sm outline-none focus:border-orange-500";

const BulkPrice = ({ k }) => {
  const [price, setPrice] = useState("");
  const [brand, setBrand] = useState("");
  const apply = async () => {
    if (price === "" || Number(price) < 0) return toast.error("Įveskite kainą");
    if (!window.confirm(`Nustatyti ${price} € be PVM kainą ${brand || "visoms"} sėdynėms, kuriose įjungtas šis priedas?`)) return;
    try {
      const { data } = await api.post(`/admin/option-defs/${k}/price`, { price: Number(price), brand });
      toast.success(`Kaina atnaujinta ${data.updated} sėdynėms`);
      setPrice("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="sm:col-span-2 flex flex-wrap items-center gap-2 border-t border-[#3B4157] pt-3">
      <span className="text-xs uppercase tracking-wider text-slate-400">Kaina be PVM</span>
      <input data-testid={`option-def-${k}-price`} type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="€" className={`${input.replace("w-full ", "")} w-28`} />
      <select data-testid={`option-def-${k}-price-brand`} value={brand} onChange={(e) => setBrand(e.target.value)} className={`${input.replace("w-full ", "")} w-44`}>
        <option value="">Visoms sėdynėms</option>
        {BRANDS.map((b) => <option key={b} value={b}>Tik {b}</option>)}
      </select>
      <button data-testid={`option-def-${k}-price-apply`} onClick={apply} className="h-10 px-4 border border-[#3B4157] text-sm hover:border-orange-500 hover:text-orange-500">Taikyti</button>
    </div>
  );
};

const OptionCard = ({ def, onSaved }) => {
  const [f, setF] = useState(def);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(def), [def]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { setF({ ...f, image: (await api.post("/admin/upload", fd)).data.path }); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const { key, ...body } = f;
      await api.put(`/admin/option-defs/${key}`, body);
      toast.success("Išsaugota");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`option-def-${f.key}`} className="border border-[#3B4157] bg-[#292D3C] p-5 grid sm:grid-cols-[112px_1fr] gap-5">
      <label className="w-28 h-28 border border-dashed border-slate-600 bg-white grid place-items-center cursor-pointer hover:border-orange-500 overflow-hidden relative">
        {f.image ? <img src={imgUrl(f.image)} alt="" className="w-full h-full object-cover" /> : <Upload className="w-5 h-5 text-slate-500" />}
        <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-center text-white py-0.5">Keisti</span>
        <input data-testid={`option-def-${f.key}-upload`} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid={`option-def-${f.key}-name-lt`} className={input} value={f.name_lt} onChange={set("name_lt")} placeholder="Pavadinimas LT" />
        <input data-testid={`option-def-${f.key}-name-en`} className={input} value={f.name_en} onChange={set("name_en")} placeholder="Name EN" />
        <textarea data-testid={`option-def-${f.key}-desc-lt`} rows={2} className={`${input} h-auto py-2`} value={f.desc_lt} onChange={set("desc_lt")} placeholder="Aprašymas LT" />
        <textarea data-testid={`option-def-${f.key}-desc-en`} rows={2} className={`${input} h-auto py-2`} value={f.desc_en} onChange={set("desc_en")} placeholder="Description EN" />
        <BulkPrice k={f.key} />
        <button data-testid={`option-def-${f.key}-save`} onClick={save} disabled={busy} className="sm:col-span-2 justify-self-end h-10 px-5 bg-orange-500 text-[#1F2230] text-sm font-display font-bold uppercase tracking-wider hover:bg-orange-400 disabled:opacity-60 inline-flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Išsaugoti
        </button>
      </div>
    </div>
  );
};

export default function AdminOptions() {
  const [defs, load] = useOptionDefs();
  return (
    <div data-testid="admin-options" className="space-y-6">
      <h1 className="font-display font-black uppercase text-4xl tracking-tight">Priedai</h1>
      <p className="text-sm text-slate-400">Priedų nuotraukos ir aprašymai rodomi prie sėdynių. Kainą visoms (arba vieno gamintojo) sėdynėms nustatykite laukeliu „Kaina be PVM“ ir „Taikyti“. Atskirai sėdynei kainą galite pakeisti jos redagavimo lange.</p>
      <div className="space-y-4">{OPTION_KEYS.filter((k) => defs[k]).map((k) => <OptionCard key={k} def={defs[k]} onSaved={load} />)}</div>
    </div>
  );
}
